import { useState } from 'react';
import { AlertCircle, CheckCircle, Loader, GitMerge, Search } from 'lucide-react';
import { getCsrfToken } from '../utils/csrfToken';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export function LookupMemberPanel() {

  // ── Member Lookup state ──────────────────────────────────────────────────
  const [lookupQuery, setLookupQuery] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState(null);
  const [lookupData, setLookupData] = useState(null);
  const [disambiguation, setDisambiguation] = useState(null);

  // ── Merge state ───────────────────────────────────────────────────────────
  const [mergeOldId, setMergeOldId] = useState('');
  const [mergeNewId, setMergeNewId] = useState('');
  const [mergeConfirmed, setMergeConfirmed] = useState(false);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [mergeError, setMergeError] = useState(null);
  const [mergeResult, setMergeResult] = useState(null);

  // ── Lookup handler ────────────────────────────────────────────────────────
  const handleLookupMember = async () => {
    if (!lookupQuery.trim()) return;
    setLookupLoading(true);
    setLookupError(null);
    setLookupData(null);
    setDisambiguation(null);

    try {
      const token = await getCsrfToken();
      const response = await fetch(`${API_URL}/api/admin/lookup-member`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token
        },
        body: JSON.stringify({ query: lookupQuery.trim() })
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Lookup failed');
      }

      const data = await response.json();
      
      if (data.status === 'no_results') {
        setLookupError(data.message);
      } else if (data.status === 'multiple_matches') {
        setDisambiguation(data);
      } else if (data.status === 'success') {
        setLookupData(data);
      }
      
    } catch (err) {
      setLookupError(err.message || 'Lookup failed');
    } finally {
      setLookupLoading(false);
    }
  };

  // ── Handle selection from disambiguation ─────────────────────────────────
  const handleSelectPerson = async (personId) => {
    setLookupQuery(personId);
    setDisambiguation(null);
    
    // Auto-trigger lookup with selected person ID
    setLookupLoading(true);
    setLookupError(null);
    
    try {
      const token = await getCsrfToken();
      const response = await fetch(`${API_URL}/api/admin/lookup-member`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token
        },
        body: JSON.stringify({ query: personId })
      });

      if (!response.ok) {
        throw new Error('Lookup failed');
      }

      const data = await response.json();
      setLookupData(data);
      
    } catch (err) {
      setLookupError(err.message || 'Lookup failed');
    } finally {
      setLookupLoading(false);
    }
  };

  // ── Merge handler ─────────────────────────────────────────────────────────
  const handleMerge = async () => {
    if (!mergeOldId.trim() || !mergeNewId.trim()) return;
    setMergeLoading(true);
    setMergeError(null);

    try {
      const token = await getCsrfToken();
      const response = await fetch(`${API_URL}/api/admin/merge-members`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token
        },
        body: JSON.stringify({
          oldPersonId: mergeOldId.trim(),
          newPersonId: mergeNewId.trim()
        })
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Merge failed');
      }

      const data = await response.json();
      setMergeResult(data);
    } catch (err) {
      setMergeError(err.message || 'Merge failed');
    } finally {
      setMergeLoading(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-8">

      {/* ========== MEMBER LOOKUP SECTION ========== */}
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Lookup Member</h2>
          <p className="text-gray-600 text-sm">
            Search by person ID, name, email, or phone number.
          </p>
        </div>

        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-900">
            <span className="font-semibold">Smart search:</span> Type anything - person ID, name, email, or phone. We'll find the right CBA member.
          </p>
        </div>

        {lookupError && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{lookupError}</p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={lookupQuery}
            onChange={(e) => setLookupQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleLookupMember()}
            placeholder="Person ID, name, email, or phone..."
            disabled={lookupLoading}
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 text-sm"
          />
          <button
            onClick={handleLookupMember}
            disabled={lookupLoading || !lookupQuery.trim()}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2 transition-colors"
          >
            {lookupLoading ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Searching...
              </>
            ) : (
              <>
                <Search className="w-4 h-4" />
                Lookup
              </>
            )}
          </button>
        </div>

        {/* Disambiguation UI - Multiple Matches */}
        {disambiguation && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="font-semibold text-yellow-900 mb-3">{disambiguation.message}</p>
            <div className="space-y-2">
              {disambiguation.matches.map((person, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSelectPerson(person.id)}
                  className="w-full flex items-center justify-between p-3 bg-white hover:bg-yellow-50 border border-yellow-300 rounded-lg transition-colors text-left"
                >
                  <div>
                    <div className="font-semibold text-gray-900">{person.name}</div>
                    <div className="text-sm text-gray-600">
                      {person.email !== 'No email' && <span>{person.email}</span>}
                      {person.email !== 'No email' && person.phone !== 'No phone' && <span className="mx-2">•</span>}
                      {person.phone !== 'No phone' && <span>{person.phone}</span>}
                    </div>
                  </div>
                  <div className="font-mono text-sm text-blue-600 font-medium">
                    {person.id} →
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Results Display */}
        {lookupData && (
          <div className="space-y-4">
            
            {/* Search Context Banner */}
            {lookupData.resolvedFrom && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <div className="text-sm text-green-900">
                    <span className="font-semibold">Found:</span> {lookupData.resolvedFrom.name}
                    {lookupData.resolvedFrom.email && <span className="ml-2 text-green-700">({lookupData.resolvedFrom.email})</span>}
                    <span className="ml-2 text-green-700">→ Person ID: {lookupData.personId}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Household + Credit Summary */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1">Household Size</p>
                <p className="text-2xl font-bold text-gray-800">{lookupData.householdMembers.length}</p>
                <p className="text-xs text-gray-500 mt-1">{lookupData.householdMembers.join(', ')}</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1">Total Available</p>
                <p className="text-2xl font-bold text-green-700">${lookupData.reportMath.totalAvailable.toFixed(2)}</p>
                <p className="text-xs text-gray-500 mt-1">Payments + Credit</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1">Credit Balance</p>
                <p className="text-2xl font-bold text-blue-700">${lookupData.creditBalance.displayCredit.toFixed(2)}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Start: ${lookupData.creditBalance.startingCredit.toFixed(2)} | Calc: ${lookupData.creditBalance.calculatedCredit.toFixed(2)}
                </p>
              </div>
            </div>

            {/* Report Math Breakdown */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-yellow-900 mb-2">Report Math (what the owing report calculates)</p>
              <div className="space-y-1 text-sm text-yellow-800 font-mono">
                <p>totalExpected  = {lookupData.reportMath.householdSize} people × eventCount × $20</p>
                <p>totalAvailable = ${lookupData.reportMath.totalPayments.toFixed(2)} payments + ${lookupData.reportMath.totalCreditBalance.toFixed(2)} credit = <span className="font-bold">${lookupData.reportMath.totalAvailable.toFixed(2)}</span></p>
                <p>amountOwing    = totalExpected − ${lookupData.reportMath.totalAvailable.toFixed(2)}</p>
              </div>
              <p className="text-xs text-yellow-700 mt-2 italic">Event count is fetched live by the report — compare against past-only count to check for mismatch.</p>
            </div>

            {/* Household Payment Rows */}
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">
                Household Payments ({lookupData.payments.householdRows.length} rows)
                <span className="font-normal text-gray-500 ml-2">— this is what the report sums</span>
              </p>
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold">Person ID</th>
                        <th className="px-3 py-2 text-left font-semibold">Amount</th>
                        <th className="px-3 py-2 text-left font-semibold">Completed At</th>
                        <th className="px-3 py-2 text-left font-semibold">PCO Payment ID</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {lookupData.payments.householdRows.map((row, i) => (
                        <tr key={i} className={row.payment_amount ? '' : 'bg-red-50'}>
                          <td className="px-3 py-2 font-mono text-gray-700">{row.person_id}</td>
                          <td className={`px-3 py-2 font-mono ${row.payment_amount ? 'text-gray-800' : 'text-red-600 italic'}`}>
                            {row.payment_amount ? `$${parseFloat(row.payment_amount).toFixed(2)}` : 'NULL'}
                          </td>
                          <td className={`px-3 py-2 font-mono text-xs ${row.completed_at ? 'text-gray-600' : 'text-red-600 italic'}`}>
                            {row.completed_at ? new Date(row.completed_at).toLocaleString() : 'NULL'}
                          </td>
                          <td className="px-3 py-2 font-mono text-xs text-gray-500">
                            {row.pco_payment_id || '—'}
                            {row.pco_payment_id?.startsWith('CR_') && <span className="ml-1 text-blue-600">(credit)</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="flex justify-between mt-2 text-xs text-gray-500">
                <span><span className="inline-block w-3 h-3 bg-red-50 border border-red-200 rounded mr-1"></span>NULL row — not counted by report</span>
                <span><span className="inline-block w-3 h-3 bg-blue-50 border border-blue-200 rounded mr-1"></span>CR_ row — credit application</span>
              </div>
            </div>

            {/* Clear Button */}
            <button
              onClick={() => { 
                setLookupData(null); 
                setLookupError(null); 
                setLookupQuery('');
              }}
              className="w-full bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 font-medium transition-colors"
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {/* ========== DIVIDER ========== */}
      <hr className="border-gray-300" />

      {/* ========== MERGE MEMBERS SECTION ========== */}
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Merge Members</h2>
          <p className="text-gray-600 text-sm">
            Merge profiles on local DB to match PCO.
          </p>
        </div>

        <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
          <p className="text-sm text-orange-900">
            <span className="font-semibold">Use case:</span> Combine duplicate profiles after PCO merge
          </p>
        </div>

        {mergeError && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-red-900">Error</p>
              <p className="text-sm text-red-800">{mergeError}</p>
            </div>
          </div>
        )}

        {!mergeResult ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Person ID to Remove <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={mergeOldId}
                  onChange={(e) => { setMergeOldId(e.target.value); setMergeConfirmed(false); }}
                  placeholder="ID no longer in PCO"
                  disabled={mergeLoading}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-400 focus:border-transparent disabled:opacity-50 font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">Old ID to remove</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Person ID to Keep <span className="text-green-600">*</span>
                </label>
                <input
                  type="text"
                  value={mergeNewId}
                  onChange={(e) => { setMergeNewId(e.target.value); setMergeConfirmed(false); }}
                  placeholder="Active ID in PCO"
                  disabled={mergeLoading}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-transparent disabled:opacity-50 font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">Active ID in PCO</p>
              </div>
            </div>

            {/* Confirmation checkbox */}
            {mergeOldId.trim() && mergeNewId.trim() && mergeOldId.trim() !== mergeNewId.trim() && (
              <label className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer">
                <input
                  type="checkbox"
                  checked={mergeConfirmed}
                  onChange={(e) => setMergeConfirmed(e.target.checked)}
                  className="accent-orange-500 mt-0.5"
                />
                <p className="text-sm text-gray-700">
                  I confirm: remove all rows for <span className="font-mono font-semibold text-red-600">{mergeOldId.trim()}</span>, payments already under <span className="font-mono font-semibold text-green-600">{mergeNewId.trim()}</span>. <span className="font-semibold">This cannot be undone.</span>
                </p>
              </label>
            )}

            <button
              onClick={handleMerge}
              disabled={
                !mergeOldId.trim() ||
                !mergeNewId.trim() ||
                mergeOldId.trim() === mergeNewId.trim() ||
                !mergeConfirmed ||
                mergeLoading
              }
              className="w-full bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2 transition-colors"
            >
              {mergeLoading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Merging...
                </>
              ) : (
                <>
                  <GitMerge className="w-5 h-5" />
                  Run Merge
                </>
              )}
            </button>
          </div>
        ) : (
          <div>
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-green-900 mb-2">{mergeResult.message}</p>
                  <div className="space-y-1 text-sm text-green-800">
                    <p><span className="font-semibold">Payments migrated:</span> {mergeResult.details?.paymentsMigrated || 0}</p>
                    <p><span className="font-semibold">Duplicates removed:</span> {mergeResult.details?.paymentsDuped || 0}</p>
                    <p><span className="font-semibold">Placeholders dropped:</span> {mergeResult.details?.paymentsDropped || 0}</p>
                    <p><span className="font-semibold">Credit consolidated:</span> {mergeResult.details?.creditConsolidated ? 'Yes' : 'No'}</p>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={() => {
                setMergeResult(null);
                setMergeOldId('');
                setMergeNewId('');
                setMergeConfirmed(false);
                setMergeError(null);
              }}
              className="w-full bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 font-medium transition-colors"
            >
              Merge Another
            </button>
          </div>
        )}
      </div>

    </div>
  );
}

export default LookupMemberPanel;